import { Icon } from '@iconify/react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../tasks.api';
import type { AppNotification } from '../tasks.types';
import { fmtDateTime } from '../tasks.utils';

const POLL_MS = 60_000;

/** In-app notifications: unread badge, a list, and a jump to the task or queue they concern. */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<AppNotification[]>([]);

  const refreshCount = useCallback(() => {
    notificationsApi.unreadCount().then((r) => setCount(r.count)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    const t = window.setInterval(refreshCount, POLL_MS);
    return () => window.clearInterval(t);
  }, [refreshCount]);

  const open = async (el: HTMLElement) => {
    setAnchor(el);
    try {
      setItems((await notificationsApi.list(false, 0, 15)).content);
    } catch {
      setItems([]);
    }
  };

  const follow = async (n: AppNotification) => {
    if (!n.read) await notificationsApi.read(n.id).catch(() => {});
    setAnchor(null);
    refreshCount();
    if (n.entityType === 'task' && n.entityId) navigate(`/tasks/${n.entityId}`);
    else if (n.entityType === 'task_reassignment') navigate('/tasks/reassignments');
    else if (n.entityType === 'timesheet') navigate('/timesheet');
  };

  const readAll = async () => {
    await notificationsApi.readAll().catch(() => {});
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    <>
      <IconButton aria-label='Notifications' onClick={(e) => open(e.currentTarget)}>
        <Badge color='error' badgeContent={count} max={99}>
          <Icon icon='mdi:bell-outline' width={26} />
        </Badge>
      </IconButton>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Stack sx={{ width: 360, maxHeight: 480 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ px: 2, py: 1 }}>
            <Typography fontWeight={600}>Notifications</Typography>
            <Button size='small' onClick={readAll} disabled={count === 0}>Mark all read</Button>
          </Stack>
          <Box sx={{ overflowY: 'auto' }}>
            {items.length === 0 && <Typography sx={{ px: 2, py: 3 }} color='text.secondary'>Nothing yet.</Typography>}
            {items.map((n) => (
              <Box key={n.id} onClick={() => follow(n)}
                sx={{ px: 2, py: 1.25, cursor: 'pointer', borderTop: 1, borderColor: 'divider', bgcolor: n.read ? undefined : 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}>
                <Typography variant='body2' fontWeight={n.read ? 400 : 600}>{n.title}</Typography>
                {n.message && <Typography variant='caption' display='block' color='text.secondary'>{n.message}</Typography>}
                <Typography variant='caption' color='text.secondary'>{fmtDateTime(n.createdAt)}</Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
